// client/src/components/doctors/Appointments/BillingPaymentSection.jsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import billingService from '../../../services/billingService';
import ViewPaymentConfirmation from './ViewPaymentConfirmation';
import '../../../styles/components/doctor/BillingPaymentSection.css';
import BankDropdown from '../../common/BankDropdown';

// Import icons
import exportPdfIcon from '../../../assets/icons/indicators/EXPORT_PDF.svg';
import searchIcon from '../../../assets/icons/indicators/search_icon.svg';
import paymentModalIndicator from '../../../assets/icons/indicators/Indicator_Payment_Modal.png';

// Payment setup modal component
const PaymentSetupModal = ({ 
  isOpen, 
  onClose, 
  paymentMethods, 
  setPaymentMethods,
  handleQRCodeUpload,
  newBankAccount, 
  setNewBankAccount,
  handleAddBankAccount,
  handleRemoveBankAccount,
  handleSavePaymentMethods
}) => {
  if (!isOpen) return null;

  return (
    <div className="doctor-appointments-modal-overlay">
      <div className="doctor-appointments-modal doctor-appointments-large-modal">
        <div className="doctor-appointments-payment-modal-header">
          <div className="doctor-appointments-payment-header-left">
            <button 
              className="doctor-appointments-back-btn"
              onClick={onClose}
            >
              <span className="doctor-appointments-back-arrow">‹</span>
            </button>
            
            <h3 className="doctor-appointments-payment-modal-title">Add Payment Option</h3>
          </div>
            
          <div className="doctor-appointments-payment-icon-container">
            <img 
              src={paymentModalIndicator} 
              alt="Payment Options" 
              className="doctor-appointments-payment-header-icon"
            />
          </div>
        </div>
        
        <div className="doctor-appointments-modal-body">
          <div className="doctor-appointments-payment-setup-section">
            <div className="doctor-appointments-digital-payment-section-container">
              <div className="doctor-appointments-digital-payment-header">
                <h4 className="doctor-appointments-modal-digital-payment-title">Digital Payment QR Codes</h4>
                <p className="doctor-appointments-modal-digital-payment-subtitle">Upload QR codes for your digital payment accounts</p>
              </div>
              
              <div className="doctor-appointments-digital-payment-grid">
                {/* GCash Upload Container */}
                <div className="doctor-appointments-payment-upload-container">
                  <label className="doctor-appointments-payment-upload-label">GCash</label>
                  <div className="doctor-appointments-qr-upload-area">
                    {paymentMethods?.gcash?.qrCode ? (
                      <div className="doctor-appointments-qr-preview-container">
                        <img 
                          src={paymentMethods.gcash.qrCode} 
                          alt="GCash QR" 
                          className="doctor-appointments-qr-preview-image" 
                        />
                        <div className="doctor-appointments-qr-overlay">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleQRCodeUpload('gcash', e.target.files[0])}
                            className="doctor-appointments-qr-file-input"
                            id="gcash-upload"
                          />
                          <label htmlFor="gcash-upload" className="doctor-appointments-qr-change-btn">
                            Change Image
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="doctor-appointments-qr-upload-placeholder">
                        <div className="doctor-appointments-upload-icon">+</div>
                        <span className="doctor-appointments-upload-text">GCash QR Code</span>
                        <span className="doctor-appointments-upload-subtext">Click or drag image here</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleQRCodeUpload('gcash', e.target.files[0])}
                          className="doctor-appointments-qr-file-input"
                          id="gcash-upload-new"
                        />
                        <label htmlFor="gcash-upload-new" className="doctor-appointments-qr-upload-btn">
                          Upload Image
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* PayMaya Upload Container */}
                <div className="doctor-appointments-payment-upload-container">
                  <label className="doctor-appointments-payment-upload-label">Maya</label>
                  <div className="doctor-appointments-qr-upload-area">
                    {paymentMethods?.paymaya?.qrCode ? (
                      <div className="doctor-appointments-qr-preview-container">
                        <img 
                          src={paymentMethods.paymaya.qrCode} 
                          alt="PayMaya QR" 
                          className="doctor-appointments-qr-preview-image" 
                        />
                        <div className="doctor-appointments-qr-overlay">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleQRCodeUpload('paymaya', e.target.files[0])}
                            className="doctor-appointments-qr-file-input"
                            id="paymaya-upload"
                          />
                          <label htmlFor="paymaya-upload" className="doctor-appointments-qr-change-btn">
                            Change Image
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="doctor-appointments-qr-upload-placeholder">
                        <div className="doctor-appointments-upload-icon">+</div>
                        <span className="doctor-appointments-upload-text">Maya QR Code</span>
                        <span className="doctor-appointments-upload-subtext">Click or drag image here</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleQRCodeUpload('paymaya', e.target.files[0])}
                          className="doctor-appointments-qr-file-input"
                          id="paymaya-upload-new"
                        />
                        <label htmlFor="paymaya-upload-new" className="doctor-appointments-qr-upload-btn">
                          Upload Image
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          
          <div className="doctor-appointments-payment-setup-section">
            <h4 className="doctor-appointments-bank-section-title">Bank Account Details</h4>
            
            <div className="doctor-appointments-bank-account-form">
              <div className="doctor-appointments-bank-form-field">
                <label className="doctor-appointments-bank-field-label">Bank Name</label>
                <BankDropdown
                  value={newBankAccount.bankName}
                  onChange={(bankName) => setNewBankAccount(prev => ({...prev, bankName}))}
                  placeholder="Select Bank"
                  accountNumber={newBankAccount.accountNumber}
                  onCardTypeDetected={(cardType) => {
                    console.log('Detected card type:', cardType);
                  }}
                  className="doctor-appointments-bank-input-dropdown"
                />
              </div>
              
              <div className="doctor-appointments-bank-form-field">
                <label className="doctor-appointments-bank-field-label">Account Name</label>
                <input
                  type="text"
                  placeholder="Enter account holder name..."
                  value={newBankAccount.accountName}
                  onChange={(e) => setNewBankAccount(prev => ({...prev, accountName: e.target.value}))}
                  className="doctor-appointments-bank-input"
                />
              </div>
              
              <div className="doctor-appointments-bank-form-field">
                <label className="doctor-appointments-bank-field-label">Account Number</label>
                <input
                  type="text"
                  placeholder="Enter account number..."
                  value={newBankAccount.accountNumber}
                  onChange={(e) => setNewBankAccount(prev => ({...prev, accountNumber: e.target.value}))}
                  className="doctor-appointments-bank-input"
                />
              </div>
              
              <button 
                className="doctor-appointments-add-another-bank-btn"
                onClick={handleAddBankAccount}
                disabled={!newBankAccount.bankName || !newBankAccount.accountName || !newBankAccount.accountNumber}
              >
                <span className="doctor-appointments-add-icon">+</span>
                Add Another Bank Account
              </button>
            </div>
            
            {/* Display existing bank accounts */}
            {paymentMethods?.bankAccounts && paymentMethods.bankAccounts.length > 0 && (
              <div className="doctor-appointments-bank-accounts-list">
                {paymentMethods.bankAccounts.map((account) => (
                  <div key={account.id} className="doctor-appointments-bank-account-item">
                    <div className="doctor-appointments-bank-account-info">
                      <span className="doctor-appointments-bank-name">{account.bankName}</span>
                      <span className="doctor-appointments-account-name">{account.accountName}</span>
                      <span className="doctor-appointments-account-number">
                        {account.accountNumber.replace(/(\d{4})/g, '$1 ').trim()}
                      </span>
                    </div>
                    <button 
                      className="doctor-appointments-remove-bank-btn" 
                      onClick={() => handleRemoveBankAccount(account.id)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="doctor-appointments-modal-footer">
          <button className="doctor-appointments-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="doctor-appointments-confirm-btn doctor-appointments-save" onClick={handleSavePaymentMethods}>
           Save Payment Methods
         </button>
       </div>
     </div>
   </div>
  );
};

const BillingPaymentSection = ({ 
  patients, 
  initialBillingData = [], 
  onBillingDataChange,
  onError
}) => {
  // States
  const [billingData, setBillingData] = useState(initialBillingData);
  const [billingSearchQuery, setBillingSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState('');
  const [billingStatusFilter, setBillingStatusFilter] = useState('all');
  const [showPaymentSetupModal, setShowPaymentSetupModal] = useState(false);
  const [showPaymentConfirmationModal, setShowPaymentConfirmationModal] = useState(false);
  const [selectedBillingRecord, setSelectedBillingRecord] = useState(null);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Payment setup states
  const [paymentMethods, setPaymentMethods] = useState({
    gcash: { qrCode: null, enabled: false },
    paymaya: { qrCode: null, enabled: false },
    bankAccounts: []
  });
  
  const [newBankAccount, setNewBankAccount] = useState({
    bankName: '',
    accountName: '',
    accountNumber: ''
  });
  
  // If initialBillingData changes (from parent), update our state
  useEffect(() => {
    if (initialBillingData && initialBillingData.length > 0) {
      setBillingData(initialBillingData);
    }
  }, [initialBillingData]);
  
  // Fetch billing data when component mounts (only if no initialBillingData)
  useEffect(() => {
    if (!initialBillingData || initialBillingData.length === 0) {
      fetchBillingData();
    }
    fetchPaymentMethods();
  }, [initialBillingData]);
  
  const fetchBillingData = async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Starting billing data fetch...');
      
      const response = await billingService.getBillingRecords();
      
      console.log('📊 Billing response:', response);
      
      if (response && response.success && response.data) {
        console.log('✅ Setting billing data with', response.data.length, 'records');
        setBillingData(response.data);
        
        // Notify parent component about the change if callback provided
        if (onBillingDataChange) {
          onBillingDataChange(response.data);
        }
      } else {
        console.log('❌ Invalid response structure');
        setError('Failed to fetch billing data: Invalid response');
        
        // Notify parent about error if callback provided
        if (onError) {
          onError('Failed to fetch billing data: Invalid response');
        }
      }
    } catch (err) {
      console.error('❌ BILLING FETCH ERROR:', err);
      setError('Failed to fetch billing data: ' + err.message);
      
      // Notify parent about error if callback provided
      if (onError) {
        onError('Failed to fetch billing data: ' + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch payment methods with improved error handling
  const fetchPaymentMethods = async () => {
    try {
      console.log('🔍 Starting payment methods fetch...');
      const response = await billingService.getPaymentMethods();
      
      console.log('📊 Payment methods response:', response);
      
      if (response && response.success && response.data) {
        console.log('✅ Setting payment methods:', response.data);
        setPaymentMethods(response.data);
      } else {
        console.log('⚠️ Invalid payment methods response, using defaults');
        // Keep existing payment methods or set defaults
        setPaymentMethods(prevMethods => prevMethods || {
          gcash: { qrCode: null, enabled: false },
          paymaya: { qrCode: null, enabled: false },
          bankAccounts: []
        });
      }
    } catch (err) {
      console.error('❌ PAYMENT METHODS FETCH ERROR:', err);
      console.error('❌ Error name:', err.name);
      console.error('❌ Error message:', err.message);
      
      // Check if this is a network error
      if (err.response) {
        console.error('❌ Server responded with error:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
      } else if (err.request) {
        console.error('❌ Network error - no response received:', err.request);
      }
      
      // Keep existing payment methods or set defaults
      setPaymentMethods(prevMethods => prevMethods || {
        gcash: { qrCode: null, enabled: false },
        paymaya: { qrCode: null, enabled: false },
        bankAccounts: []
      });
    }
  };
  
  // Handle confirming payment
  const handleConfirmPayment = async () => {
    if (!selectedBillingRecord) return;
    
    try {
      setIsConfirmingPayment(true);
      console.log('🔄 Confirming payment for billing record:', selectedBillingRecord.id);
      
      const response = await billingService.markAsPaid(selectedBillingRecord.id, {
        confirmedBy: 'doctor',
        confirmedAt: new Date().toISOString(),
        notes: 'Payment manually confirmed by doctor'
      });
      
      if (response.success) {
        // Update the billing data state to reflect the change
        const updatedData = billingData.map(record => 
          record.id === selectedBillingRecord.id 
            ? { ...record, status: 'paid' } 
            : record
        );
        
        setBillingData(updatedData);
        
        // Notify parent component about the change if callback provided
        if (onBillingDataChange) {
          onBillingDataChange(updatedData);
        }
        
        // Close the modal
        setShowPaymentConfirmationModal(false);
        setSelectedBillingRecord(null);
        
        // Show success message
        console.log('✅ Payment confirmed successfully');
      } else {
        throw new Error(response.message || 'Failed to confirm payment');
      }
    } catch (error) {
      console.error('❌ Error confirming payment:', error);
      setError('Failed to confirm payment: ' + error.message);
      
      // Notify parent about error if callback provided
      if (onError) {
        onError('Failed to confirm payment: ' + error.message);
      }
    } finally {
      setIsConfirmingPayment(false);
    }
  };
  
  // Handle QR code upload
  const handleQRCodeUpload = async (type, file) => {
    if (!file) return;
    
    try {
      console.log(`Uploading ${type} QR code...`);
      const response = await billingService.uploadQRCode(type, file);
      
      if (response.success) {
        setPaymentMethods(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            qrCode: response.qrCodeUrl,
            enabled: true
          }
        }));
        console.log(`${type} QR code uploaded successfully`);
      }
    } catch (error) {
      console.error(`Error uploading ${type} QR code:`, error);
      setError(`Failed to upload ${type} QR code`);
    }
  };

  // Handle adding bank account
  const handleAddBankAccount = async () => {
    if (!newBankAccount.bankName || !newBankAccount.accountName || !newBankAccount.accountNumber) {
      setError('Please fill in all bank account fields');
      return;
    }
    
    try {
      console.log('Adding bank account:', newBankAccount);
      const response = await billingService.addBankAccount(newBankAccount);
      
      if (response.success) {
        setPaymentMethods(prev => ({
          ...prev,
          bankAccounts: [...(prev.bankAccounts || []), response.bankAccount]
        }));
        
        setNewBankAccount({
          bankName: '',
          accountName: '',
          accountNumber: ''
        });
        
        console.log('Bank account added successfully');
      }
    } catch (error) {
      console.error('Error adding bank account:', error);
      setError('Failed to add bank account');
    }
  };

  // Handle removing bank account
  const handleRemoveBankAccount = async (accountId) => {
    try {
      console.log('Removing bank account:', accountId);
      const response = await billingService.removeBankAccount(accountId);
      
      if (response.success) {
        setPaymentMethods(prev => ({
          ...prev,
          bankAccounts: (prev.bankAccounts || []).filter(account => account.id !== accountId)
        }));
        
        console.log('Bank account removed successfully');
      }
    } catch (error) {
      console.error('Error removing bank account:', error);
      setError('Failed to remove bank account');
    }
  };

  // Save payment methods
  const handleSavePaymentMethods = async () => {
    try {
      console.log('Saving payment methods:', paymentMethods);
      const response = await billingService.updatePaymentMethods(paymentMethods);
      
      if (response.success) {
        console.log('Payment methods saved successfully');
        setShowPaymentSetupModal(false);
        
        setTimeout(() => {
          setError(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error saving payment methods:', error);
      setError('Failed to save payment methods');
    }
  };
  
  // View payment details
  const viewPaymentDetails = (billingRecord) => {
    setSelectedBillingRecord(billingRecord);
    setShowPaymentConfirmationModal(true);
  };
  
  // Export billing data to PDF
  const exportBillingToPDF = async () => {
    try {
      console.log('📄 Exporting billing data to PDF...');
      
      // Filter billing data based on current filters
      const filteredData = getFilteredBillingData();
      
      // Call the export function from billing service
      await billingService.exportBillingToPDF(filteredData);
      
      console.log('✅ PDF exported successfully');
    } catch (error) {
      console.error('❌ Error exporting to PDF:', error);
      setError('Failed to export billing data: ' + error.message);
      
      // Notify parent about error if callback provided
      if (onError) {
        onError('Failed to export billing data: ' + error.message);
      }
    }
  };
  
 // Get filtered billing data
const getFilteredBillingData = () => {
  return billingData
    .filter(bill => {
      // Apply search filter
      if (billingSearchQuery.trim() !== '') {
        const query = billingSearchQuery.toLowerCase();
        if (!bill.patientName?.toLowerCase().includes(query)) {
          return false;
        }
      }
      
      // Apply patient filter
      if (selectedPatient && bill.patientId !== selectedPatient) {
        return false;
      }
      
      // Apply status filter
      if (billingStatusFilter !== 'all' && bill.status !== billingStatusFilter) {
        return false;
      }
      
      return true;
    })
    // ✅ SORT BY CREATION TIME - MOST RECENTLY ADDED FIRST
    .sort((a, b) => {
      // Try to sort by createdAt timestamp first
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      
      // Fallback: Sort by MongoDB ObjectId (which contains timestamp)
      // Newer ObjectIds have higher values
      if (a.id && b.id) {
        return b.id.localeCompare(a.id);
      }
      
      // Last fallback: Use _id
      if (a._id && b._id) {
        return b._id.localeCompare(a._id);
      }
      
      // If all else fails, sort by appointment date descending
      return new Date(b.appointmentDate) - new Date(a.appointmentDate);
    });
};
  
  // Get billing status color class
const getBillingStatusColor = (status) => {
  // Normalize the status to lowercase and handle underscores
  const normalizedStatus = status?.toLowerCase().replace(/_/g, '_');
  
  switch (normalizedStatus) {
    case 'paid': 
      return 'billing-paid';
    case 'pending': 
      return 'billing-pending';
    case 'proof_uploaded': 
    case 'proofuploaded':
      return 'billing-proof_uploaded';
    case 'overdue': 
      return 'billing-overdue';
    default: 
      return 'billing-default';
  }
};

const formatStatusText = (status) => {
  if (!status) return 'Unknown';
  
  // Handle specific cases
  switch (status.toLowerCase()) {
    case 'proof_uploaded':
    case 'proofuploaded':
      return 'Proof Uploaded';
    case 'paid':
      return 'Paid';
    case 'pending':
      return 'Pending';
    case 'overdue':
      return 'Overdue';
    default:
      // Capitalize first letter for any other status
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};
  
  return (
  <div className="doctor-appointments-section">
    <div className="doctor-appointments-table-container">
      <div className="doctor-appointments-section-header">
        <h2 className="doctor-appointments-section-title">Billing & Payment Information</h2>
      </div>
      
      <div className="doctor-appointments-history-filters">
  <div className="doctor-appointments-filter-top-row">
    <div className="doctor-appointments-search-bar">
      <div className="doctor-appointments-search-wrapper">
        <img 
          src={searchIcon} 
          alt="Search" 
          className="doctor-appointments-search-icon"
        />
        <input
          type="text"
          placeholder="Search billing records..."
          value={billingSearchQuery}
          onChange={(e) => setBillingSearchQuery(e.target.value)}
          className="doctor-appointments-search-input"
        />
      </div>
    </div>
    
    <button 
      className="doctor-appointments-setup-payment-btn"
      onClick={() => setShowPaymentSetupModal(true)}
    >
      Setup Payment Methods
    </button>
  </div>
  
  <div className="doctor-appointments-filter-bottom-row">
    <div className="doctor-appointments-filter-left">
      <div className="doctor-appointments-filter-group">
        <label>Patient</label>
        <select
          value={selectedPatient}
          onChange={(e) => setSelectedPatient(e.target.value)}
          className="doctor-appointments-filter-select"
        >
          <option value="">All (Default)</option>
          {patients?.map(patient => (
            <option key={patient._id} value={patient._id}>
              {patient.firstName} {patient.lastName}
            </option>
          ))}
        </select>
      </div>
      
      <div className="doctor-appointments-filter-group">
        <label>Status</label>
        <select
          value={billingStatusFilter}
          onChange={(e) => setBillingStatusFilter(e.target.value)}
          className="doctor-appointments-filter-select"
        >
          <option value="all">All (Default)</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>
    </div>
    
    <button 
      className="doctor-appointments-export-btn"
      onClick={exportBillingToPDF}
    >
      <img 
        src={exportPdfIcon} 
        alt="Export" 
        className="doctor-appointments-export-icon"
      />
      Export PDF
    </button>
  </div>
</div>
      
      {isLoading ? (
        <div className="doctor-appointments-empty-state">
          <p>Loading billing records...</p>
        </div>
      ) : error ? (
        <div className="doctor-appointments-error-alert">
          <span className="doctor-appointments-alert-icon">⚠️</span>
          {error}
          <button 
            className="doctor-appointments-action-btn"
            onClick={fetchBillingData}
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="doctor-appointments-table">
          <div className="doctor-appointments-table-header">
            <div className="doctor-appointments-header-cell">Appointment Schedule</div>
            <div className="doctor-appointments-header-cell">Patient Name</div>
            <div className="doctor-appointments-header-cell">Session Fee</div>
            <div className="doctor-appointments-header-cell">Billing Status</div>
            <div className="doctor-appointments-header-cell">Action</div>
          </div>
          
          {getFilteredBillingData().length === 0 ? (
            <div className="doctor-appointments-empty-state">
              <p>No billing records found</p>
            </div>
          ) : (
            getFilteredBillingData().map((bill) => (
              <div key={bill.id || bill._id} className="doctor-appointments-table-row">
                <div className="doctor-appointments-table-cell">
                  <div className="doctor-appointments-date-time-info">
                    <span className="doctor-appointments-date-text">
                      {(() => {
                        // Extract date parts directly from string to avoid timezone conversion
                        const match = bill.appointmentDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
                        if (!match) return 'Invalid date';
                        
                        const year = parseInt(match[1]);
                        const month = parseInt(match[2]) - 1; // JS months are 0-indexed
                        const day = parseInt(match[3]);
                        
                        // Create date in local timezone but don't use the time part
                        const localDate = new Date(year, month, day);
                        return format(localDate, 'MMM dd, yyyy');
                      })()}
                    </span>
                    <span className="doctor-appointments-time-range">
                      {(() => {
                        // Extract time directly from string to avoid timezone conversion
                        const match = bill.appointmentDate.match(/T(\d{2}):(\d{2})/);
                        if (!match) return 'Invalid time';
                        
                        const hours = parseInt(match[1]);
                        const minutes = match[2];
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        const hour12 = hours % 12 || 12;
                        
                        return `${hour12}:${minutes} ${ampm}`;
                      })()}
                    </span>
                  </div>
                </div>
                
                <div className="doctor-appointments-table-cell">
                  <span className="doctor-appointments-patient-name">{bill.patientName}</span>
                </div>
                
                <div className="doctor-appointments-table-cell">
                  <span className="doctor-appointments-session-fee">
                    ₱{(bill.sessionFee || 0).toLocaleString()}
                  </span>
                </div>
                
                <div className="doctor-appointments-table-cell">
                  <span className={`doctor-appointments-status-badge doctor-appointments-${getBillingStatusColor(bill.status)}`}>
                    {formatStatusText(bill.status)}
                  </span>
                </div>
                
                <div className="doctor-appointments-table-cell doctor-appointments-actions-cell">
                  <button 
                    className="doctor-appointments-action-btn doctor-appointments-view-btn"
                    onClick={() => viewPaymentDetails(bill)}
                    title="View Details"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9Z" fill="currentColor"/>
                      <path fillRule="evenodd" clipRule="evenodd" d="M12 5C7.52166 5 3.73646 7.94288 2.45789 12C3.73646 16.0571 7.52166 19 12 19C16.4783 19 20.2635 16.0571 21.5421 12C20.2635 7.94288 16.4783 5 12 5ZM12 17C9.23858 17 7 14.7614 7 12C7 9.23858 9.23858 7 12 7C14.7614 7 17 9.23858 17 12C17 14.7614 14.7614 17 12 17Z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
    
    {/* Payment Confirmation Modal */}
    {showPaymentConfirmationModal && selectedBillingRecord && (
      <ViewPaymentConfirmation
        payment={selectedBillingRecord}
        onClose={() => {
          setShowPaymentConfirmationModal(false);
          setSelectedBillingRecord(null);
        }}
        onConfirmPayment={handleConfirmPayment}
        isLoading={isConfirmingPayment}
      />
    )}
    
    {/* Payment Setup Modal */}
    {showPaymentSetupModal && (
      <PaymentSetupModal
        isOpen={showPaymentSetupModal}
        onClose={() => setShowPaymentSetupModal(false)}
        paymentMethods={paymentMethods}
        setPaymentMethods={setPaymentMethods}
        handleQRCodeUpload={handleQRCodeUpload}
        newBankAccount={newBankAccount}
        setNewBankAccount={setNewBankAccount}
        handleAddBankAccount={handleAddBankAccount}
        handleRemoveBankAccount={handleRemoveBankAccount}
        handleSavePaymentMethods={handleSavePaymentMethods}
      />
    )}
  </div>
  );
};

export default BillingPaymentSection;